import * as ko from "knockout";
import {ICustomSystemAccess, IComboboxOptions_SystemRole} from "@component/combobox/system-role";
import {
    AbstractDialogOperation,
    IParams as AbstractDialogOperationParams,
    Step
} from "@component/object/dialog/AbstractDialogOperation";
import {TServiceName} from "../../../constants/ApiServices";
import {nsObjectSearch} from "@component/object/nsObjectSearch";
import {nsRuleset} from "@component/ruleset/nsRuleset";
import {ObjectTypeId} from "../../../constants/ObjectTypeId";
import {searchObjectWithSyncfields} from "../../../util/request";
import {global, lm} from "../../../appContext";
import {MAX_RESULTS_PER_PAGE, SPINNER} from "../../../constants";
import {IMessageData, IObjectData, IRule, ISearchObjectParams} from "../../../index";
import {ISortableTableParams} from "@component/sortable";
import {nsSyncFields} from "../../../req/nsSyncFields";
import {createErrorMessage, getRawSyncFieldValue} from "../../../util/misc";
import {REQUEST_MESSAGE} from "../../../constants/Language";
import {ButtonManager} from "../../../util/ButtonManager";
import {getCancelButton, getNextButton} from "../../../constants/RouteDefinitions";
import {_} from 'lodash';
import PromiseRequest from "../../../req/PromiseRequest";
import TObjectData_basic = nsSyncFields.TObjectData_basic;
import remove_manual_permissionHtml from './remove.manual.permission.html?raw';

export const template = remove_manual_permissionHtml;

interface IParams extends AbstractDialogOperationParams {
    roleid?: number
    systemid?: number
}

export class viewModel extends AbstractDialogOperation {

    protected serviceName: TServiceName = 'updatepermission';
    private readonly comboboxParams_systemRole: IComboboxOptions_SystemRole;
    private readonly selectedPermission: KnockoutObservable<ICustomSystemAccess | null> = ko.observable(null)
    private readonly data_included: KnockoutObservableArray<IObjectData> = ko.observableArray();
    private readonly data_excluded: KnockoutObservableArray<IObjectData> = ko.observableArray();
    private readonly messageData_stepInit: KnockoutObservable<IMessageData | null> = ko.observable(null);
    protected override submitError: { message: string, id: number, obj: IObjectData }[] = []

    private readonly pageButtons: ButtonManager = new ButtonManager()

    constructor(protected override params: IParams) {
        super(params)

        const roleid = this.params?.roleid
        const systemid = this.params?.systemid
        let initialMatch: ((o: ICustomSystemAccess) => boolean) | undefined
        if (roleid || systemid) {
            if (roleid) {
                initialMatch = o => o.role?.id == roleid
            } else if (systemid) {
                initialMatch = o => o.system?.id == systemid
            }
        }

        this.comboboxParams_systemRole = {
            comboboxOptions: {
                inputAttrs: {placeholder: lm.t('none selected')},
                selected: this.selectedPermission,
                ...(initialMatch ? {initialMatch} : {}),
                option: {
                    disabled: o => o.type == 'system' || !!o.system.definitionreadonly,
                },
                onSelect: (o) => {
                    if (!o) {
                        this.messageData_stepInit(null)
                        this.data_included.removeAll()
                        this.data_excluded.removeAll()
                        return
                    }
                    this.doSearchManualPermissions()
                },
            },
            showIcon: true,
        }

        this.pageButtons.add(getCancelButton(() => 1, {
            attr: {'data-dismiss': "modal"},
            enabled: () => this.step() !== Step.Process,
            icon: () => this.isStep(Step.Complete) ? 'fa-times' : 'fa-ban',
            label: () => this.isStep(Step.Complete) ? 'close' : 'cancel',
        }))

        this.pageButtons.add(getNextButton({
            onClick: () => this.step(Step.DownloadIds),
            enabled: () => !!this.data_included().length,
            label: 'yes, continue',
            validator: () => this.isStep(Step.Init)
        }))
    }

    protected getProcessItemSuccessMessage(): string {
        return `((updating|td)) [${this.submitSuccess.length + this.submitError.length}/${this.data_included().length}] ${SPINNER}`;
    }

    protected successMessage(): string {
        const n = this.submitSuccess.length
        return `subject is removed|n:${n}|s:${this.getNumObjectsLabel(n)}`
    }

    protected errorMessage(): string {
        const n = this.submitError.length
        return `subject is not removed due to error|n:${n}|s:${this.getNumObjectsLabel(n)}`
    }

    protected override getNumObjectsLabel(num: number): string {
        return `${num} ((permission|ac|n:${num}))`
    }

    private doSearchManualPermissions() {
        this.messageData_stepInit({message: REQUEST_MESSAGE})

        const selectedPermission = this.selectedPermission();
        if (!selectedPermission) {
            return;
        }

        const rule1: IRule = nsObjectSearch.getSearchUserManualPermissionRule(
            selectedPermission.type,
            selectedPermission.role?.id ?? selectedPermission.system?.id,
            {
                order: 1
            }
        )

        const rule2: IRule = nsRuleset.generateRule({
            argument: this.params.data().map(o => o.id).join(','),
            argumentoperator: 'Inneholder en av',
            attribute: 'Id',
            booleanoperator: 'AndAnd',
            order: 2,
        })

        const ruleset = nsRuleset.generateRuleset({
            rules: [rule1, rule2],
        })
        const params: Partial<ISearchObjectParams> = {
            jruleset: JSON.stringify(ruleset),
            resultsprpage: MAX_RESULTS_PER_PAGE
        }

        Promise.resolve()
            .then(() => {
                return searchObjectWithSyncfields(ObjectTypeId.User, ['Id', 'GivenName', 'Surname'], params)
                    .then((data: string[][] | IObjectData[]) => {
                        const objectData = data as IObjectData[];
                        this.data = objectData.map(o => o.id)
                        const data_excluded = this.params.data().filter(o => !this.data.includes(o.id))
                        this.data_included(objectData)
                        this.data_excluded(data_excluded)
                        const numSelection = this.params.data().length
                        const numIncluded = this.data_included().length
                        if (!!numIncluded) {
                            this.messageData_stepInit({
                                message: `Users have this manual permission|n:${numIncluded}|num:${numIncluded}|total:${numSelection}`
                            })
                        } else {
                            this.messageData_stepInit({
                                message: `No users in the selection have this manual permission|total:${numSelection}`,
                                type: 'warning',
                            })
                        }
                    })
            })
            .catch((reason: string) => {
                this.step(null)
                this.messageData(createErrorMessage(String(reason)))
            })
    }

    protected override getPromiseForProcessId(callback: () => Promise<any>, id: number): Promise<any> {
        return Promise.resolve()
            .then(() => {
                const promiseRequest = new PromiseRequest('getobject');
                this.promiseRequests.push(promiseRequest)
                return promiseRequest.request({
                    id: id,
                    manual: false,
                })
            })
            .then((data: [IObjectData]) => {
                const objectData = _.cloneDeep(data[0])

                // retrieve the org
                const org = objectData
                    .permission.find((o: IObjectData['permission'][number]) => o.organizationid == global.login.selectedOrganizationId());
                if (!org) {
                    return callback()
                }

                // remove the manual permission
                const systemPermissionToBeRemoved = org
                    .systempermission.find((sp: IObjectData['permission'][number]['systempermission'][number]) => sp.role.id == this.params.roleid)
                const indexToBeRemoved = org.systempermission.findIndex((sp: IObjectData['permission'][number]['systempermission'][number]) => sp == systemPermissionToBeRemoved)
                if (indexToBeRemoved >= 0) {
                    org.systempermission.splice(indexToBeRemoved, 1)
                }

                const submitObject: Partial<IObjectData> = {
                    id: id,
                    permission: objectData.permission,
                }

                const promiseRequest = new PromiseRequest('updatepermission');
                this.promiseRequests.push(promiseRequest)
                return promiseRequest
                    .request({jobject: JSON.stringify(submitObject)})
                    .then(() => {
                        this.submitSuccess.push(id)
                        return callback()
                    })
                    .catch((reason: string) => {
                        this.submitError.push({
                            id: id,
                            message: reason,
                            obj: objectData,
                        })
                        return callback()
                    })
            })
    }

    protected override onProcessComplete() {
        global.cacheManager.removeCache('searchobject')
        global.cacheManager.removeCache('getobject')
        this.step(Step.Complete);
    }


    private readonly tableParams: ISortableTableParams<TObjectData_basic> = {
        TABLE: [{
            key: 'id',
            sort: true,
        }, {
            key: 'GivenName',
            sort: true,
            getContent: (o: TObjectData_basic) => getRawSyncFieldValue('GivenName', o.syncfields) ?? '',
        }, {
            key: 'Surname',
            sort: true,
            getContent: (o: TObjectData_basic) => getRawSyncFieldValue('Surname', o.syncfields) ?? '',
        }],
        asc: ko.observable(true),
        data: this.data_included as KnockoutObservableArray<TObjectData_basic>,
    }

    private readonly isDataReady = ko.pureComputed((): boolean => !!this.data_included().length || !!this.data_excluded().length)


}
